import { AppConfigResponse } from '../types/config';
import { DOG_BREEDS } from '../utils/breeds';

export const DEFAULT_APP_CONFIG: AppConfigResponse = {
  pricing_rules: {
    initiation_days: 14,
    subscription_days: 28,
    // Deben coincidir con el default del backend (dynamic_config/defaults.py)
    // y con producción. Modelo confirmado por Santiago (2026-06-12): pack de
    // iniciación -20%, suscripción mensual -30%.
    initiation_discount: 0.2,
    monthly_discount: 0.3,
  },
  nutrition_rules: {
    calorie_multiplier: 0.8156,
    min_daily_kcal: 120,
    max_daily_kcal: 5000,
    cachorro_age_caps_months: {
      small: 12,
      medium: 15,
      large: 18,
      giant: 24,
    },
    // Espejo de nutrition_rules.k_model del backend (dynamic_config/defaults.py).
    // Solo es fallback: el backend siempre envía los valores reales.
    k_model: {
      base_coeff: 110,
      weight_exponent: 0.75,
      k_activity: { bajo: 0.9, medio: 1.0, alto: 1.15 },
      k_bcs: { delgado: 1.1, ideal: 1.0, sobrepeso: 0.9 },
      k_age_tiers: [
        { max_months: 3, k: 1.75 },
        { max_months: 8, k: 1.5 },
        { max_months: 12, k: 1.3 },
        { max_months: 18, k: 1.2 },
        { max_months: 83, k: 1.0 },
      ],
      k_age_senior: 0.85,
      puppy_max_months: 12,
      young_adult_max_months: 24,
      safety_floor_coeff: 55,
      safety_ceiling_coeff: 300,
      neutered_factor: 0.85,
      neutered_min_age_months: 12,
    },
  },
  exclusion_rules: {
    blocked_pathologies: [],
    blocked_protein_types: [],
    blocked_product_ids: [],
    pathology_blocked_protein_types: {},
    pathology_blocked_product_ids: {},
    protein_aliases: {},
    pathology_aliases: {},
    pathology_rules: {},
    allergen_rules: {},
  },
  pack_rules: {
    allow_custom_initiation_distribution: true,
    allow_recipe_removal_in_checkout: true,
    min_monthly_cans: 1,
    max_open_pack_hours: 72,
    always_allowed_pack_size_g: 400,
    conditional_pack_size_g: 800,
    target_kcal_tolerance_percent: 3,
    maximum_kcal_excess_percent: 7,
    maximum_kcal_shortfall_percent: 3,
    energy_score_weight: 1,
    distribution_score_weight: 1,
    nutritional_equivalence_epsilon_percent: 0.1,
    candidate_radius_units: 3,
    default_energy_distribution: {
      pollo_adulto: 0.35,
      cerdo_adulto: 0.2,
      ternera_pavo_adulto: 0.2,
      pavo_adulto: 0.25,
      pollo_cachorro: 0.35,
      cerdo_cachorro: 0.2,
    },
    rounding_recipe_priority: {
      pollo_adulto: 0,
      cerdo_adulto: 1,
      pavo_adulto: 2,
      ternera_pavo_adulto: 3,
      pollo_cachorro: 0,
      cerdo_cachorro: 1,
    },
  },
  inventory_rules: {
    enabled: true,
    mode: 'inventory_first_then_storefront',
    primary_location_id: '',
    fail_open: true,
    cache_ttl_seconds: 30,
    request_timeout_seconds: 3,
    test_mode_enabled: false,
    test_overrides: {},
    prestashop_enabled: true,
    prestashop_fail_open: true,
    stock_reserve_scope: 'first_order',
  },
  geo_rules: {
    enabled: true,
    supported_countries: ['ES'],
    fallback_country: null,
    ip_lookup_enabled: false,
    rate_limit_alert_email_enabled: false,
  },
  // Producto Shopify "Donación Patitas&Rescue" (handle
  // donacion-patitas-rescue, id 16075804770639). El mismo variant 1€
  // (57576474607951) sirve para donación puntual y para recurrente
  // mensual — Appstle aplica el selling-plan de suscripción cuando el
  // line item viene con donationMode='recurring' (mismo patrón que las
  // recetas de comida natural, que reutilizan variant id + selling plan).
  donation_rules: {
    enabled: true,
    one_time_variant_ids: {
      '1': '57576474607951',
      '5': '57576474640719',
      '10': '57576474673487',
      '20': '57576474706255',
      '50': '57576474739023',
      custom: null,
    },
    recurring_variant_id: '57576474607951',
    min_eur: 1,
    max_eur: 1000,
    info_url: 'https://miloylola.com/products/donacion-patitas-rescue',
  },
  multipet_rules: {
    enabled: true,
  },
  runtime_controls: {
    maintenance_enabled: false,
    maintenance_title: 'Estamos haciendo una mejora rápida',
    maintenance_message: 'La calculadora estará disponible de nuevo en unos minutos.',
    maintenance_cta_label: 'Contactar por WhatsApp',
    // Empty: the screen falls back to the link derived from
    // contact_rules.whatsapp_phone. Set it only to point the CTA elsewhere.
    maintenance_cta_url: '',
    checkout_paused: false,
    checkout_paused_message: 'Estamos revisando el checkout. Déjanos tus datos y te ayudamos por WhatsApp.',
    prestashop_checkout_enabled: false,
    prestashop_disabled_title: 'Muy pronto en Patitas&co',
    prestashop_disabled_message:
      'Estamos terminando de preparar la comida natural en esta tienda. Mientras tanto puedes pedirla en miloylola.com.',
    full_funnel_enabled: false,
    checkout_client_side_enabled: false,
    banner: {
      enabled: false,
      rotation_seconds: 5,
      desktop_texts: [
        'Envío refrigerado en 24/48h según zona',
        'Planes personalizados por nutricionistas veterinarios',
      ],
      mobile_texts: ['Envío 24/48h según zona', 'Plan personalizado para tu perro'],
      background_color: '#008BBC',
      text_color: '#FFFFFF',
      link_label: '',
      link_url: '',
    },
  },
  contact_rules: {
    // No number here on purpose: it lives in the backoffice so it is never
    // hardcoded in the repo. Empty = no WhatsApp offered anywhere.
    whatsapp_phone: '',
    whatsapp_display: '',
  },
  chat_widget_rules: {
    // 'livechat' keeps today's behaviour until an operator switches it.
    active_widget: 'livechat',
    whatsapp: {
      message_template: 'Hola, tengo una duda sobre {nombre|mi peludo}. Estoy en el paso: {paso}',
      position: 'bottom-right',
      offset_x_px: 16,
      offset_y_px: 16,
      background_color: '#25D366',
      icon_color: '#FFFFFF',
      size_px: 56,
      label: '',
      show_close_button: true,
      delay_seconds: 0,
      visible_steps: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14],
    },
  },
  shipping_rules: {
    enabled: false,
    default_country: 'ES',
    unsupported_message: 'Ahora mismo no enviamos a tu zona.',
    blocked_postal_code_patterns: [],
    countries: [],
  },
  funnel_config: {
    dog_breeds: DOG_BREEDS,
    pathology_options: [
      {
        value: 'ninguna',
        label: 'Ninguna',
        description: 'Sin patologías conocidas',
        icon: '✅',
        incompatible: false,
      },
      {
        value: 'alergias_alimentarias',
        label: 'Alergias alimentarias',
        description: 'Reacciones adversas',
        icon: '🌾',
        incompatible: false,
      },
      {
        value: 'sensibilidad_digestiva',
        label: 'Problemas digestivos',
        description: 'IBD, gastritis, diarreas...',
        icon: '🫧',
        incompatible: false,
        sub_pathologies: [
          { value: 'ibd', label: 'IBD' },
          { value: 'gastritis_reflujo', label: 'Gastritis / reflujo' },
          { value: 'heces_blandas', label: 'Heces blandas / diarreas recurrentes' },
          { value: 'estrenimiento', label: 'Estreñimiento' },
        ],
      },
      {
        value: 'sobrepeso_obesidad',
        label: 'Sobrepeso',
        description: 'Control de peso',
        icon: '⚖️',
        incompatible: false,
      },
      {
        value: 'problemas_articulares',
        label: 'Articulaciones',
        description: 'Artritis o displasia',
        icon: '🦴',
        incompatible: false,
        sub_pathologies: [
          { value: 'artritis', label: 'Artritis' },
          { value: 'displasia', label: 'Displasia' },
          { value: 'osteoartritis', label: 'Osteoartritis' },
        ],
      },
      {
        value: 'piel_pelo',
        label: 'Piel y pelo',
        description: 'Dermatitis, picores',
        icon: '✨',
        incompatible: false,
        sub_pathologies: [
          { value: 'alopecia', label: 'Alopecia' },
          { value: 'dermatitis', label: 'Dermatitis' },
          { value: 'prurito', label: 'Prurito' },
        ],
      },
      {
        value: 'dental',
        label: 'Problemas dentales',
        description: 'Sarro o gingivitis',
        icon: '🦷',
        incompatible: false,
        sub_pathologies: [
          { value: 'sarro_mal_aliento', label: 'Sarro / mal aliento' },
          { value: 'gingivitis', label: 'Gingivitis' },
          { value: 'hipoplasia_dental', label: 'Hipoplasia dental' },
        ],
      },
      {
        value: 'renal',
        label: 'Enfermedad renal',
        description: 'Requiere dieta veterinaria',
        icon: '⚠️',
        incompatible: true,
      },
      {
        value: 'hepatica',
        label: 'Enfermedad hepática',
        description: 'Requiere dieta veterinaria',
        icon: '⚠️',
        incompatible: true,
      },
      {
        value: 'pancreatica',
        label: 'Enfermedad pancreática',
        description: 'Requiere dieta veterinaria',
        icon: '⚠️',
        incompatible: true,
      },
    ],
    protein_allergy_options: [
      {
        value: 'ninguna',
        label: 'Ninguna alergia',
        description: 'Tolera todas las proteínas',
        icon: '✅',
      },
      {
        value: 'pollo',
        label: 'Pollo',
        description: 'Alergia o intolerancia',
        icon: '🐔',
      },
      {
        value: 'ternera',
        label: 'Ternera',
        description: 'Alergia o intolerancia',
        icon: '🐄',
      },
      {
        value: 'cordero',
        label: 'Cordero',
        description: 'Alergia o intolerancia',
        icon: '🐑',
      },
      {
        value: 'pescado',
        label: 'Salmón',
        description: 'Alergia o intolerancia',
        icon: '🐟',
      },
      {
        value: 'pavo',
        label: 'Pavo',
        description: 'Alergia o intolerancia',
        icon: '🦃',
      },
      {
        value: 'conejo',
        label: 'Conejo',
        description: 'Alergia o intolerancia',
        icon: '🐰',
      },
      {
        value: 'cerdo',
        label: 'Cerdo',
        description: 'Alergia o intolerancia',
        icon: '🐷',
      },
      {
        value: 'pato',
        label: 'Pato',
        description: 'Alergia o intolerancia',
        icon: '🦆',
      },
      {
        value: 'huevo',
        label: 'Huevo',
        description: 'Alergia o intolerancia',
        icon: '🥚',
      },
      {
        value: 'marisco_moluscos',
        label: 'Mariscos / moluscos',
        description: 'Alergia o intolerancia',
        icon: '🦐',
      },
      {
        value: 'algas',
        label: 'Algas',
        description: 'Alergia o intolerancia',
        icon: '🌿',
      },
      {
        value: '',
        label: 'Caballo',
        description: 'Próximamente',
        icon: '🐎',
        disabled: true,
      },
    ],
    allowed_product_ids_perro: [],
    legal_urls: {
      terms: 'https://miloylola.com/policies/terms-of-service',
      privacy: 'https://miloylola.com/policies/privacy-policy',
    },
    support_email: 'hola@miloylola.com',
    draft_ttl_days: 90,
    translations: {
      // ── Navigation ───────────────────────────────────────────────────────
      'layout.back': 'Atrás',
      'layout.continue': 'Continuar',

      // ── Calculadora de calorías (modo kcal) — editables en "Contenido Funnel" ──
      'flavor_select.title': '¿De qué sabores quieres calcular la ración de {nombre}?',
      'flavor_select.subtitle': 'Elige uno o varios y te decimos los gramos de cada uno.',
      'flavor_select.missing': 'Elige al menos un sabor',
      'flavor_select.no_recipes': 'No hay sabores disponibles ahora mismo.',
      'resultkcal.daily_need': '{nombre} necesita al día',
      'resultkcal.avg_note': 'de media entre {n} sabores',
      'resultkcal.today_title': '¿Qué le quieres dar hoy?',
      'resultkcal.today_help': 'Toca los sabores del día para ver sus gramos.',
      'resultkcal.pick_one': 'Elige al menos un sabor para ver los gramos.',
      'resultkcal.transition_title': 'Periodo de transición ({dias} días)',
      'resultkcal.transition_help':
        'Cámbiala poco a poco. Gramos de Milo y Lola por sabor (el resto del plato, su comida actual):',
      'resultkcal.transition_none': 'Puedes darle su ración completa desde el primer día.',
      'resultkcal.ration_title': 'Su ración diaria',
      'resultkcal.ration_help_single': 'Dándole solo este sabor, esta es su ración completa del día:',
      'resultkcal.ration_help_multi': 'Repartida entre los sabores de hoy:',
      'resultkcal.ration_help_mixed': 'Gramos de Milo y Lola (en dieta mixta, el resto del plato es su comida actual):',
      'resultkcal.total': 'Total del día',

      // ── My account ───────────────────────────────────────────────────────
      'account.status.verifying': 'Validando tu enlace…',
      'account.status.loading': 'Cargando tu cuenta…',
      'account.error.title': 'Algo no fue bien',
      'account.error.new_link_cta': 'Pedir un nuevo enlace',
      'account.common.close': 'Cerrar',
      'account.common.cancel': 'Cancelar',
      'account.common.confirm': 'Confirmar',
      'account.auth.title': 'Tu cuenta',
      'account.auth.subtitle': 'Introduce tu email o móvil para entrar — sin contraseña.',
      'account.auth.identifier_placeholder': 'tu@email.com o 612 345 678',
      'account.auth.phone_placeholder': '612 345 678',
      'account.auth.email_invalid': 'Introduce un email válido.',
      'account.auth.sending': 'Enviando…',
      'account.auth.sms_cta': 'Enviar código SMS',
      'account.auth.email_cta': 'Enviar enlace',
      'account.auth.email_sent_title': 'Revisa tu email 📬',
      'account.auth.email_sent_subtitle':
        'Si tu email está registrado, te hemos enviado un enlace de acceso. Caduca en 7 días.',
      'account.auth.otp_title': 'Introduce el código',
      'account.auth.otp_subtitle_prefix': 'Te hemos enviado un código de 6 dígitos a',
      'account.auth.otp_subtitle_suffix': 'Caduca en 10 minutos.',
      'account.auth.otp_resend_cta': 'Reenviar código',
      'account.auth.otp_resend_countdown': 'Reenviar código en {seconds}s',
      'account.auth.otp_resend_error': 'No pudimos reenviar el código.',
      'account.dashboard.title': 'Mi cuenta',
      'account.dashboard.logout': 'Salir',
      'account.dashboard.greeting': 'Hola',
      'account.dashboard.empty_title': '👋 Aún no tienes ningún plan activo.',
      'account.dashboard.empty_cta': 'Crear plan para mi peludo →',
      'account.toast.pet_added_named': '¡{petName} ya forma parte de tu plan!',
      'account.toast.pet_added_generic': '¡Nueva mascota añadida a tu plan!',
      'account.toast.pet_added_subtitle': 'Entrará en tu próxima renovación junto con el resto de tu familia.',
      'account.subscription.status_active': '🐾 Tu plan activo',
      'account.subscription.status_paused': '⏸ Plan pausado',
      'account.subscription.next_charge': 'Próximo cargo: {date}',
      'account.subscription.skip_cta': '⏭ Saltar pedido',
      'account.subscription.pause_cta': '⏸ Pausar',
      'account.subscription.resume_cta': '▶ Reanudar',
      'account.subscription.cancel_support_cta': 'Cancelar suscripción',
      'account.subscription.skip_confirm_dates':
        'Tu próximo cargo pasará del {from} al {to}. No habrá envío este ciclo.',
      'account.subscription.skip_confirm_days':
        'El próximo pedido se retrasará {days} días. No habrá envío este ciclo.',
      'account.subscription.pause_error': 'No pudimos pausar el plan. Inténtalo de nuevo.',
      'account.subscription.resume_error': 'No pudimos reanudar el plan. Inténtalo de nuevo.',
      'account.subscription.skip_error': 'No pudimos saltar el pedido. Inténtalo de nuevo.',
      'account.subscription.add_pet_cta': '➕ Añadir otro peludo',
      'account.subscription.support_only_message': 'Para cambios en este plan, escríbenos y lo revisamos contigo.',
      'account.pet.legacy_subtitle': 'Tu perro actual',

      // ── P1 — Bienvenida ──────────────────────────────────────────────────
      'step1.dog_label': 'Perro',
      'step1.cat_label': 'Gato',
      'step1.cat_aria': 'Gato — próximamente',
      'step1.cat_coming_soon': 'Gato · Próximamente',
      'step1.name_label': '¿Cómo se llama tu peludo?',
      'step1.name_placeholder': 'ej: Max, Bella, Milo...',
      'p1.title': 'Crea el menú personalizado para tu peludo',
      'p1.title_mobile': 'Crea el menú personalizado para tu peludo',
      'p1.title_addon': '¿Cómo se llama el nuevo peludo?',
      'p1.time': 'Te llevará 1 minuto',
      'p1.trust_label': 'Formulado por veterinarios nutricionistas',
      'p1.vet_team_title': 'Nuestro equipo veterinario',
      'p1.vet_team_body':
        'Cada receta de Milo y Lola está formulada y revisada por veterinarios especializados en nutrición canina. La ración de tu perro se calcula a medida según su peso, edad, actividad y condición corporal.',
      'p1.vet_team_body_2': '',
      'p1.vet_team_close': 'Entendido',
      'p1.addon_banner': 'Añadiendo otro peludo a tu plan',
      'p1.multipet_question': '¿Tienes más de un peludito?',
      'p1.multipet_yes': 'Sí, tengo varias',
      'p1.multipet_no': 'Solo una',
      'p1.multipet_modal_title': 'Vamos una mascota cada vez',
      'p1.multipet_modal_body':
        'Empezamos con esta mascota. Al terminar su plan, podrás añadir otra antes de pagar, sin repetir pasos innecesarios.',
      'p1.multipet_modal_note': 'Cada mascota podrá tener sus propias recetas y suplementos.',
      'p1.multipet_modal_cta': 'Perfecto, seguimos',
      'p1.multipet_disabled_note':
        '¿Tienes más de una mascota? Al terminar, repite el test para crear su plan: cada peludo tendrá su propia suscripción.',
      'p1.missing_animal': 'Selecciona la especie de tu mascota',
      'p1.missing_name': 'Escribe el nombre para continuar',
      'p2.adult_senior_hint': 'A partir de los 7 años solemos considerarlo senior. ¿Quieres marcarlo como senior?',
      'stepnav.recetas': 'RECETAS',
      'stepnav.recetas_mobile': 'RECETAS',
      'stepnav.plan': 'TU PLAN',
      'stepnav.plan_mobile': 'TU PLAN',
      'stepnav.extras': 'SUPLEMENTOS',
      'stepnav.extras_mobile': 'SUPLEMENTOS',
      'stepnav.checkout': 'CHECKOUT',
      'stepnav.checkout_mobile': 'CHECKOUT',

      // ── P2 — Datos básicos (edad, sexo, esterilización) ──────────────────
      'p2.edad_title': '¿Qué edad tiene {nombre}?',
      'p2.age_years_label': 'años',
      'p2.age_months_label': 'meses',
      'p2.gender_title': '¿Es macho o hembra?',
      'p2.gender_male': 'Macho',
      'p2.gender_female': 'Hembra',
      'p2.neutered_title': '¿Está esterilizado/a {nombre}?',
      'p2.neutered_title_macho': '¿Está esterilizado {nombre}?',
      'p2.neutered_title_hembra': '¿Está esterilizada {nombre}?',
      'p2.neutered_yes': 'Sí',
      'p2.neutered_no': 'No',
      'p2.neutered_hint_compact': 'Ajustamos las kcal a su esterilización',
      'p2.neutered_yes_bullet1': 'Ración un 10-15% menor por menor gasto basal',
      'p2.neutered_yes_bullet2': 'Proteína magra para preservar masa muscular',
      'p2.neutered_yes_bullet3': 'Vigilamos el peso para prevenir sobrepeso',
      'p2.neutered_no_bullet1': 'Metabolismo natural — sin ajuste calórico',
      'p2.neutered_no_bullet2': 'Energía para hormonas y celo / monta',
      'p2.neutered_no_bullet3': 'Recalculamos si se esteriliza más adelante',
      'p2.micro_rango_bullet1': 'Etapa actual: {rango}',
      'p2.micro_rango_bullet2': 'Energía sostenida adecuada a esta edad',
      'p2.micro_rango_bullet3': 'Suplementos ajustados al perfil de la etapa',
      'p2.missing_age': 'Indica la edad para continuar',
      'p2.missing_gender': 'Selecciona el sexo',
      'p2.missing_neutered': 'Indica si está esterilizado/a',

      // ── P3 — Raza ────────────────────────────────────────────────────────
      'p3.title': '¿Cuál es la raza de {nombre}?',
      'p3.search_placeholder': 'Busca la raza de {nombre}...',
      'p3.no_results': 'No encontramos esa raza — prueba con otra búsqueda',
      'p3.popular_label': 'Más comunes',
      'p3.more_breeds_label': 'Más razas',
      'p3.mestizo_chip': 'Mestizo / Cruce',
      'p3.unknown_chip': 'No lo sé',
      'p3.alphabet_separator': 'O',
      'p3.alphabet_label': 'Buscar por inicial de la raza',
      'p3.alphabet_hint': 'Toca la primera letra para ver las razas que empiezan por ella.',
      'p3.letter_aria': 'Razas que empiezan por {letter}',
      'p3.manual_breed_unavailable': 'No podemos buscar en la lista ahora mismo, pero puedes escribir la raza.',
      'p3.use_manual_breed': 'Usar "{raza}"',
      'p3.weak_points_title': 'Puntos a vigilar en {raza}',
      'p3.change_breed': 'Cambiar raza',
      'p3.switch_to_adulto': 'Sí, cambiar a adulto',
      'p3.back_to_age': 'Volver al paso de edad',
      'p3.missing_breed': 'Selecciona la raza de {nombre}',

      // ── P4 — Peso y complexión ────────────────────────────────────────────
      'p4.title': '¿Cuánto pesa {nombre}?',
      'p4.body_title': '¿Qué complexión tiene {nombre}?',
      'p4.peso_decrease_aria': 'Reducir peso',
      'p4.peso_increase_aria': 'Aumentar peso',
      'p4.peso_input_aria': 'Peso en kg',
      'p4.slider_aria': 'Peso de {nombre} en kg',
      'p4.peso_min': '0.5 kg',
      'p4.peso_max': '80 kg',
      'p4.weight_invalid': 'Introduce un peso entre {min} y {max} kg.',
      'p4.why_delgado': 'Con estos datos calcularemos las kcal diarias para que coja peso de forma saludable.',
      'p4.why_ideal': 'Con estos datos calculamos las kcal diarias y los gramos de cada receta.',
      'p4.why_sobrepeso': 'Con estos datos calcularemos las kcal diarias para que baje de peso de forma saludable.',
      'p4.missing_peso': 'Indica el peso de {nombre}',
      'p4.missing_body': 'Selecciona la complexión',

      // ── P5 — Actividad ───────────────────────────────────────────────────
      'p5.title': '¿Cuánto ejercicio hace {nombre}?',
      'p5.why_bajo_b1': 'Ración un 5-10% menor: gasto basal reducido',
      'p5.why_bajo_b2': 'Proteína de calidad para preservar masa muscular',
      'p5.why_medio_b1': 'Kcal estándar: equilibrio actividad/descanso',
      'p5.why_medio_b2': 'Mezcla óptima de proteína magra y grasas saludables',
      'p5.why_alto_b1': 'Ración un 15-25% mayor por alto gasto energético',
      'p5.why_alto_b2': 'Más calorías de grasas buenas para resistencia',
      'p5.missing_activity': 'Selecciona el nivel de actividad',

      // ── P6 — Tipo de comida actual ────────────────────────────────────────
      'p6.food_title': '¿Qué come {nombre} ahora?',
      'p6.food_hint': 'Selecciona una o varias opciones',
      'p6.food_no_lo_se': 'No lo sé',
      'p6.why': 'Selecciona qué come {nombre} hoy para adaptar la receta y el ritmo de transición.',
      'p6.why_seca_b1': 'Transición progresiva 7-10 días desde pienso seco',
      'p6.why_cocinada_b1': 'Recetas cocinadas a baja temperatura, similar a lo que ya come',
      'p6.why_mixta_b1': 'Reemplazamos el plato gradualmente sin retirar el pienso de golpe',
      'p6.why_raw_b1': 'Mantenemos el aporte natural — proteína sin procesar',
      'p6.why_common_b1': 'Recetas adaptadas al hábito digestivo de {nombre}',
      'p6.why_common_b2': 'Sin ingredientes añadidos: solo lo que necesita',
      'p6.why_no_lo_se':
        'Sin problema — aplicamos la transición estándar para que el sistema digestivo de {nombre} se adapte sin sustos.',
      'p6.missing_food': 'Indica qué come {nombre} ahora',

      // ── P7 — Alergias ─────────────────────────────────────────────────────
      'p7.title': '¿Hay algún ingrediente que {nombre} deba evitar?',
      'p7.subtitle': 'Marca solo alergias o intolerancias conocidas.',
      'p7.gate_no': 'No',
      'p7.gate_si': 'Sí',
      'p7.tooltip_toggle': 'Más información sobre alergias e intolerancias',
      'p7.tooltip_body': 'Alergia = reacción inmediata. Intolerancia = molestia digestiva continuada.',
      'p7.tooltip_close': 'Cerrar',
      'p7.recipe_counter': 'Con esta selección, {nombre} tiene {recipe_count_label}.',
      'p7.recipe_count_singular': '1 receta que le sentará genial',
      'p7.recipe_count_plural': '{count} recetas que le sentarán genial',
      'p7.remove_allergen_aria': 'Quitar {label}',
      'p7.otros': 'Otros',
      'p7.otros_count': 'Otros ({count})',
      'p7.otros_title': 'Otras alergias o intolerancias',
      'p7.otros_search': 'Buscar...',
      'p7.otros_section_proteinas': 'Proteínas',
      'p7.otros_section_vegetales': 'Ingredientes vegetales',
      'p7.otros_done': 'Hecho',
      'p7.no_results': 'No se encontraron resultados',
      'p7.missing_gate': 'Indica si {nombre} tiene alguna alergia o intolerancia',
      'p7.missing_selection': 'Selecciona al menos un ingrediente',

      // ── P8 — Salud ────────────────────────────────────────────────────────
      'p8.gate_title': '¿Tiene {nombre} alguna enfermedad diagnosticada?',
      'p8.gate_subtitle': 'Enfermedades, intolerancias digestivas, problemas de piel, etc.',
      'p8.gate_si': 'Sí',
      'p8.gate_no': 'No',
      'p8.gate_no_se': 'No lo sé',
      'p8.why_link': '¿Por qué lo pedimos?',
      'p8.why_modal_title': '¿Por qué lo pedimos?',
      'p8.why_modal_body':
        'Algunas enfermedades requieren ajustes específicos en la dieta. Con esta información personalizamos las recetas y cantidades para que {nombre} esté lo mejor posible.',
      'p8.why_modal_close': 'Entendido',
      'p8.insights_label': 'Lo que hace nuestra comida',
      'p8.insights_modal_close': 'Cerrar',
      'p8.insights_supplement_prefix': 'Suplemento sugerido:',
      'p8.insights_mobile_trigger': 'Ver qué hace nuestra comida por {nombre}',
      'p8.otra_button': '¿No encuentras la condición de {nombre}?',
      'p8.otra_button_mobile': 'Otra condición',
      'p8.otra_chip_label': 'Otra condición',
      'p8.otra_modal_title': '¿Qué condición tiene {nombre}?',
      'p8.otra_modal_subtitle': 'Si vemos alguna compatibilidad con nuestras recetas, te llamaremos.',
      'p8.otra_search_label': 'Nombre de la enfermedad o condición',
      'p8.otra_search_placeholder': 'Ej: luxación de rótula, megaesófago, atopia…',
      'p8.otra_comment_label': 'Comentarios adicionales (opcional)',
      'p8.otra_comment_placeholder': 'Añade cualquier detalle que pueda ayudarnos…',
      'p8.otra_confirm': 'Añadir condición',
      'p8.otra_cancel': 'Cancelar',
      'p8.otra_add': 'Añadir',
      'p8.otra_excluded_title': 'Aún no tenemos una receta compatible con {condition}.',
      'p8.otra_excluded_body': 'Déjanos tu email y te avisamos cuando la desarrollemos.',
      'p8.otra_excluded_placeholder': 'tu@email.com',
      'p8.otra_excluded_submit': 'Avísame cuando esté lista',
      'p8.otra_excluded_submitting': 'Enviando…',
      'p8.otra_excluded_thanks_title': '¡Apuntado!',
      'p8.otra_excluded_thanks_body': 'Te escribiremos en cuanto tengamos una receta adaptada para {condition}.',
      'p8.otra_excluded_close': 'Cerrar',
      'p8.otra_whatsapp': 'Hablar por WhatsApp',
      'p8.otra_chip_remove_aria': 'Quitar condición',
      'p8.insights_modal_title': 'Cómo ayudará nuestra comida a {nombre}',
      'p8.tier2_title': 'Estamos preparando recetas específicas para {pathology}',
      'p8.tier2_body':
        'Aún no tenemos un plan estándar adecuado para {nombre}. Déjanos tu email y te avisaremos en cuanto esté disponible.',
      'p8.tier2_thanks_body': 'Te avisaremos en cuanto tengamos un plan adecuado para {nombre}.',
      'p8.tier2_blocking': 'Estamos preparando recetas para esa condición — déjanos tu email',
      'p8.tier2_modal_title': 'Recomendamos consultar con tu veterinario',
      'p8.tier2_continue': 'Continuar el análisis',
      'p8.tier2_email_placeholder': 'tu@email.com',
      'p8.tier2_submit': 'Avísame cuando esté listo',
      'p8.tier2_submitting': 'Enviando…',
      'p8.tier2_unselect': 'No, quitar selección',
      'p8.tier2_thanks_title': '¡Apuntado!',
      'p8.tier2_close_after_thanks': 'Cerrar',
      'p8.tier2_error': 'No pudimos enviar tu email. Inténtalo de nuevo.',
      'p8.tier3_modal_title': 'No tenemos receta compatible',
      'p8.tier3_close': 'Cerrar',
      'p8.whatsapp_pretext':
        'Hola, necesito asesoramiento nutricional para mi perro con una condición de salud especial',
      'p8.missing_gate': 'Indica si {nombre} tiene alguna condición de salud',
      'p8.missing_pathology': 'Selecciona al menos una condición',
      'p8.close_modal_aria': 'Cerrar',

      // ── P9 — Calculando el plan ────────────────────────────────────────────
      'p9.title': 'Calculando el plan de {nombre}…',
      'p9.phase1_subtitle': 'Recopilando datos de {nombre}',
      'p9.phase2_title': 'Calculando el plan de {nombre}…',
      'p9.phase2_subtitle': 'Personalizando cada detalle',
      'p9.phase2_subtitle_timeout': 'Tarda más de lo normal — preparando un plan provisional',
      'p9.fase1_label': 'Perfil de {nombre}',
      'p9.fase2_label': 'Plan nutricional',
      'p9.fase2_label_timeout': 'Afinando recomendaciones…',
      'p9.review_title': 'Plan de {nombre} listo',
      'p9.review_subtitle': 'Revisa los datos antes de elegir las recetas',
      'p9.bottom_disabled_reason': 'Calculando el plan…',

      // ── Step 9v2 — Plan ideal (resultados) ────────────────────────────────
      'step9v2.eyebrow': 'Plan preparado',
      'step9v2.title': 'El plan ideal para {nombre}',
      'step9v2.subtitle':
        'Ahora elige sus recetas favoritas. Después ajustaremos si será dieta completa o mixta y cómo empezar la transición.',
      'step9v2.kcal_label': 'Necesidad diaria estimada',
      'step9v2.kcal_value': '~{kcal} kcal/día',
      'step9v2.kcal_note':
        'Usaremos esta base junto con las recetas que selecciones para calcular gramos y precio reales.',
      'step9v2.flow.recipes': '1. Recetas',
      'step9v2.flow.diet': '2. Tipo de dieta',
      'step9v2.flow.start': '3. Cómo empezar',
      'step9v2.cta': 'Elegir recetas →',

      // ── Step 10 — Recetas ─────────────────────────────────────────────────
      'step10v2.title': 'Elige las recetas para {nombre}',
      'step10v2.subtitle': 'Alimentación natural cocinada, formulada por nuestros veterinarios nutricionistas.',
      'step10v2.selection_hint': '',
      'step10v2.stock_bounce_warning':
        'Por falta de stock no podemos servir las recetas que habías elegido. Elige otras para continuar.',
      'step10v2.recipe_of': 'Receta de {protein}',
      'step10v2.view_recipe': 'VER RECETA COMPLETA',
      'step10v2.info_aria': 'Ver detalle de la receta',
      'step10v2.info_label': 'Más info',
      'step10v2.add_to_plan': 'AÑADIR AL PLAN',
      'step10v2.added': 'AÑADIDA',
      'step10v2.continue': 'Continuar →',
      'step10.recommended': 'Recomendada',
      'step10.allergen': 'Alérgeno',
      'step10.back': 'Atrás',
      'step10.no_recipes': 'No hay recetas disponibles ahora mismo. Vuelve a intentarlo en unos minutos.',
      'step10.quantity_note': 'Las cantidades se ajustarán automáticamente según las necesidades de {nombre}.',
      'step10.excluded_reason': '{allergen} es alérgeno para {nombre}',
      'step10.unservable_badge': 'Agotado para tu pedido',
      'step10.unservable_reason': 'No hay stock suficiente de esta receta para el pedido de {nombre}.',
      'step10.no_compat_title_allergies': 'No tenemos recetas compatibles con las alergias de {nombre} ({alergias})',
      'step10.no_compat_title_generic': 'No hay recetas disponibles para {nombre}',
      'step10.no_compat_body': 'Contacta con nuestro equipo para un asesoramiento personalizado.',
      'step10.no_compat_email_intro':
        'También te podemos avisar cuando tengamos recetas compatibles si nos dejas tu email.',
      'step10.contact_whatsapp': 'Contactar por WhatsApp',
      'step10.cocinada_subtitle': 'Disponible en 400g y 800g · Proteína ~{proteinPct}%',
      'step10.recipes_cachorro_blocked_allergies_title':
        'No tenemos recetas de cachorro compatibles con las alergias de {nombre}',
      'step10.recipes_cachorro_blocked_allergies_body':
        'Ponte en contacto con nuestro equipo para asesoramiento personalizado.',
      'step10.interest_email_placeholder': 'tu@email.com',
      'step10.interest_submit': 'Avísame cuando lleguen',
      'step10.interest_submitting': 'Enviando…',
      'step10.interest_thanks_title': '¡Apuntado!',
      'step10.interest_thanks_body': 'Te avisaremos cuando haya recetas disponibles para {nombre}.',
      'step10.interest_submit_error': 'No pudimos guardar tu email. Inténtalo de nuevo.',

      // ── Step 11 — Tipo de dieta ───────────────────────────────────────────
      'step11v2.title_diet': '¿Qué tipo de dieta quieres para {nombre}?',
      'step11v2.needs': '{nombre} necesita ~{kcal} kcal al día',
      'step11v2.badge.recommended': 'Recomendado',
      'step11v2.completa.title': 'Dieta completa',
      'step11v2.completa.desc': 'Milo y Lola cubre toda la ración diaria de {nombre}.',
      'step11v2.completa.desc_mobile': 'Milo y Lola cubre toda la ración diaria de {nombre}.',
      'step11v2.mixta.title': 'Dieta mixta',
      'step11v2.mixta.desc': 'Mitad Milo y Lola y mitad su comida habitual.',
      'step11v2.mixta.desc_mobile': 'Mitad Milo y Lola y mitad su comida habitual.',
      'step11v2.card.coverage': 'Ración',
      'step11v2.card.grams': 'Gramos/día',
      'step11v2.card.kcal': 'Kcal/día',
      'step11v2.cta_diet': 'Elegir cómo empezar →',
      'step11v2.cta_loading': 'Calculando...',
      'step11v2.back': 'Atrás',

      // ── Step 12 ComoEmpezar — Inicio de suscripción ──────────────────────
      'step11v2.title_start': '¿Cómo quieres empezar el plan de {nombre}?',
      'step11v2.size.title': 'Formato de envase',
      'step11v2.size.aria': 'Elegir formato de envase',
      'step11v2.size.800_not_allowed':
        'El formato de 400 g es el más adecuado para la ración de {nombre}, para consumir el producto dentro del tiempo recomendado una vez abierto. Por eso el formato de 800 g no está disponible.',
      'step11v2.size.800_not_allowed_2': '',
      'step11v2.size.800_not_allowed_info_aria': 'Por qué este formato no es apto',
      'step11v2.size.800_not_allowed_close': 'Ocultar aviso',
      'step11v2.size.400_sub': 'más flexible',
      'step11v2.size.800_sub': 'mejor precio',
      'step11v2.size.800_sub_discount': '-{discount}% por kg',
      'step11v2.size.800_badge_discount': '-{discount}%',
      'step11v2.size.recommended_badge': 'recomendado',
      'step11v2.size.duration_warning':
        'Un envase de {value} durará ~{days} días abierto{dog_suffix}. Guárdalo en nevera o considera cambiar a {other_size}.',
      'step11v2.size.info_title': '¿Qué formato de envase conviene?',
      'step11v2.size.info_context': 'Puedes cambiarlo y el precio se recalcula al momento.',
      'step11v2.size.info_storage_title': 'Fácil de guardar',
      'step11v2.size.info_storage_body':
        'Una vez abierto, aguanta hasta 72h en nevera. En el primer pedido incluimos unas pinzas para cerrarlo bien.',
      'step11v2.size.info_price_title': 'Ahorro o flexibilidad',
      'step11v2.size.info_price_body':
        '800g sale mejor de precio. 400g es cómodo si {nombre} come poquito o prefieres abrir sobres más pequeños.',
      'stock.notice.aria': 'Recetas adaptadas por falta de stock',
      'stock.notice.intro_single':
        'Con la selección actual, tenemos que adaptar la siguiente receta por falta de stock:',
      'stock.notice.intro_plural':
        'Con la selección actual, tenemos que adaptar las siguientes recetas por falta de stock:',
      'stock.notice.size':
        'te la serviremos en formato {size}g porque no tenemos stock suficiente en formato {other}g.',
      'stock.notice.dropped':
        'te la sustituimos añadiendo más cantidad a las otras recetas seleccionadas porque no tenemos stock suficiente.',
      'stock.notice.recheck': 'Hemos actualizado el resumen; revísalo y vuelve a confirmar.',
      'stock.notice.change_before': 'Si lo prefieres, puedes volver atrás y ',
      'stock.notice.change_link': 'seleccionar otras recetas',
      'stock.notice.change_after': '.',
      'step11v2.transition.title': 'Pack de iniciación · {days} días',
      'step11v2.transition.mobile_title': 'Pack de iniciación',
      'step11v2.transition.mobile_meta': '{days} días · cambio progresivo',
      'step11v2.transition.badge': '-{discount}% descuento',
      'step11v2.transition.info_title': '¿Qué incluye el periodo de transición?',
      'step11v2.transition.info_body_v2':
        'Como {nombre} viene de {currentFood}, su transición será de {transitionDays} días: {transitionPlan}. El primer pedido cubre esa pauta y después {fullDaysInFirstOrder} días con su ración completa de Milo y Lola.',
      'step11v2.full.title': 'Pedido completo · {days} días',
      'step11v2.full.mobile_title': 'Pedido completo',
      'step11v2.full.mobile_meta': '{days} días · ración completa',
      'step11v2.full.badge': '-{discount}% descuento',
      'step11v2.full.info_title': '¿Qué pasa si empiezo con el pedido mensual?',
      'step11v2.full.info_body_v2':
        'Aunque elijas el pedido de {subscriptionDays} días, {nombre} no tiene que cambiar de golpe. Como viene de {currentFood}, empieza con esta pauta: {transitionPlan}. Después pasa a su ración completa de Milo y Lola.',
      'step11v2.combined.info_title': 'Elige cómo empezar',
      'step11v2.combined.info_context_short': 'Misma ración diaria, distinto arranque.',
      'step11v2.combined.more_info': 'Comparar las dos opciones',
      'step11v2.combined.more_info_aria': 'Más info sobre las opciones de inicio',
      'step11v2.info_context_v2': 'Adaptado a lo que come ahora y al perfil de {nombre}.',
      'step11v2.info_close': 'Cerrar',
      'step11v2.info_understood': 'Entendido',
      'step11v2.info_metric_transition': 'días de transición',
      'step11v2.info_metric_first_order': 'días primer pedido',
      'step11v2.info_metric_monthly': 'días pedido mensual',
      'step11v2.calculating': 'Calculando precio...',
      'step11v2.card.daily_price': 'Precio/día',
      'step11v2.card.total': 'Total',
      'step11v2.card.days': 'Días',
      'step11v2.card.net_days': 'Días M&L',
      'step11v2.card.net_days_more_info_aria': 'Más información sobre días comida',
      'step11v2.cta': 'Continuar →',

      // ── Step 12 Extras — Suplementos ──────────────────────────────────────
      'step12extras.title': 'Suplementos que recomendamos para {nombre}',
      'step12extras.subtitle': 'Puedes añadirlos al plan o continuar sin extras.',
      'step12extras.badge_strong': 'MUY RECOMENDADO',
      'step12extras.badge_recommended': 'RECOMENDADO',
      'step12extras.info_aria': 'Ver detalle',
      'step12extras.info_label': 'Más info',
      'step12extras.add': 'AÑADIR',
      'step12extras.added': '✓ AÑADIDO',
      'step12extras.show_more': 'Ver {count} suplementos más ↓',
      'step12extras.cta': 'Continuar →',
      'step12extras.skip': 'Continuar sin extras →',
      'step12extras.back': 'Atrás',

      // ── Step 13 — Checkout ────────────────────────────────────────────────
      'step13c.hero_title': '{nombre} está a un paso de comer como se merece',
      'step13c.hero_title_multipet': '{nombres} están a un paso de comer como se merecen',
      'step13c.title_compact': '¡Todo listo para {nombre}! 🐾',
      'step13c.title_multipet': '¡Todo listo para tu familia! 🐾',
      'step13c.title_addon': '¿Todo correcto para {nombre}? 🐾',
      'step13c.addon_banner': 'Añadiendo {nombre} a tu plan',
      'step13c.update_banner': 'Actualizando plan de {nombre}',
      'step13c.cart_title': 'Tu pedido',
      'step13c.menu_of': 'Menú de {protein}',
      'step13c.show_details': 'Ver detalle',
      'step13c.hide_details': 'Ocultar',
      'step13c.edit_menus_title': 'Editar menú',
      'step13c.edit_menus_cta': 'Editar',
      'step13c.edit_recipes': 'Editar',
      'step13c.edit_menus_kcal_label': 'Kcal del pedido',
      'step13c.edit_menus_kcal_target': 'Recomendado: {kcal}',
      'step13c.edit_menus_days_covered': 'Cubre aprox. {days} días',
      'step13c.edit_menus_days_expected': '(plan: {days} días)',
      'step13c.edit_menus_total_label': 'Total pedido',
      'step13c.reset_recipes': 'Restaurar original',
      'step13c.go_to_recipes': 'Volver a elegir recetas',
      'step13c.apply_menu_changes': 'Aplicar cambios',
      'step13c.stock_limit_hint': 'Puedes añadir hasta {n} unidades en este envío',
      'step13c.stock_out_hint': 'Agotado',
      'step13c.stock_out_blocks_apply': 'Quita la receta agotada o cámbiala de talla para poder aplicar los cambios.',
      'step13c.stock_out_blocks_apply_no_removal':
        'Cambia de talla la receta agotada o vuelve a elegir recetas para poder aplicar los cambios.',
      'step13c.cancel': 'Cancelar',
      'step13c.close': 'Cerrar',
      'step13c.transition_pack_label': 'Pack de iniciación de {nombre}',
      'step13c.full_pack_label': 'Plan completo de {nombre}',
      'step13c.extras_label': 'Suplementos',
      'step13c.multipet_disabled_note_title': '¿Tienes más de una mascota?',
      'step13c.multipet_disabled_note':
        'Completa este pedido y repite el test para la siguiente: tendrá su propia suscripción independiente.',
      'step13c.days_suffix': '{days} días',
      'step13c.discount_label': 'Dto. suscripción -{pct}%',
      'step13c.discount_monthly': 'Dto. suscripción -{pct}%',
      'step13c.discount_total': 'Descuento aplicado',
      'step13c.discount_included': '(dto incluido)',
      'step13c.products': 'Productos',
      'step13c.subtotal': 'Subtotal',
      'step13c.shipping': 'Envío',
      'step13c.shipping_enter_address': 'Introduce tu código postal',
      'step13c.free': 'Gratis',
      'step13c.total': 'Total',
      'step13c.savings': 'Ahorras {amount}',
      'step13c.monthly_from': 'Después del primer pedido',
      'step13c.monthly_discount_badge': '-{pct}% suscripcion',
      'step13c.monthly_plan': 'Plan mensual',
      'step13c.monthly_details': 'Envio gratis · Sin compromiso',
      'step13c.per_day': '{amount}/día',
      'step13c.per_month_suffix': '/mes',
      'step13c.donation_section_label': 'Donación',
      'step13c.donation_line_onetime': 'Protectora de Animales · única',
      'step13c.donation_line_recurring': 'Protectora de Animales · cada mes',
      'step13c.sticky_today': 'Total a pagar hoy',
      'step13c.sticky_month': 'Total mensual',
      'step13c.email_label': 'Email',
      'step13c.email_placeholder': 'tu@email.com',
      'step13c.email_required_hint': 'obligatorio',
      'step13c.email_valid': 'Formato correcto',
      'step13c.email_invalid_format': 'Introduce un email válido (ej: nombre@dominio.com)',
      'step13c.trust_natural_title': '100% natural',
      'step13c.trust_natural_sub': 'Sin aditivos',
      'step13c.trust_vets_title': 'Formulación veterinaria',
      'step13c.trust_vets_sub': 'Por expertos nutricionistas',
      'step13c.trust_rating_title': 'Sin frío',
      'step13c.trust_rating_sub': 'Sin complicaciones',
      'step13c.trust_cancel_title': 'Sin compromiso',
      'step13c.trust_cancel_sub': 'Pausa o cancela',
      'step13c.pay_secure_short': 'Pago seguro',
      'step13c.legal_prefix': 'Al continuar, aceptas nuestras',
      'step13c.terms_link': 'Condiciones',
      'step13c.legal_and': 'y',
      'step13c.privacy_link': 'Politica de Privacidad',
      'step13c.submit': 'CONFIRMAR PEDIDO',
      'step13c.submit_addon': 'AÑADIR A MI PLAN',
      'step13c.submit_update': 'ACTUALIZAR PLAN',
      'step13c.processing': 'Procesando...',
      'step13c.calculating': 'Actualizando precios...',
      'step13c.back': 'Volver atrás',
      'step13c.recipe_qty': 'Unidades',
      'step13c.increase_recipe_qty': 'Aumentar unidades',
      'step13c.decrease_recipe_qty': 'Reducir unidades',
      'step13c.remove_recipe_aria': 'Quitar {nombre}',
      'step13c.remove_supplement_aria': 'Quitar {nombre} del pedido',
      'step13c.remove_pet_pack_aria': 'Eliminar {nombre} del pedido',
      'step13c.remove_pet_confirm': '¿Borrar a {nombre} del pedido?',
      'step13c.remove_pet_confirm_cta': 'Borrar',
      'step13c.current_pet_fallback': 'Mascota actual',
      'step13c.no_recipe_details': 'No hay recetas seleccionadas. Vuelve a "Recetas" para configurarlas.',
      'step13c.multipet_block_title': '¿Añadimos la segunda mascota?',
      'step13c.multipet_block_cta': 'Añadir segunda mascota',
      'step13c.multipet_disabled_with_pets':
        'El pedido multi-mascota ya no está disponible. Deja solo una mascota en el carrito (puedes quitar las demás desde el resumen) y completa su pedido; después repite el test para la siguiente.',
      'step13c.multipet_more_title': '¿Quieres añadir otra mascota?',
      'step13c.multipet_more_yes': 'Sí',
      'step13c.multipet_more_no': 'No',
      'step13c.paused_title': 'Checkout temporalmente pausado',
      'step13c.paused_whatsapp_cta': 'Contactar por WhatsApp',
      'step13c.error_email': 'Por favor, introduce un email valido',
      'step13c.error_domain': 'URL de checkout no valida.',
      'step13c.error_paused':
        'Checkout pausado por mantenimiento. Inténtalo en unos minutos o contáctanos por WhatsApp.',
      'step13c.error_recipes': 'Selecciona al menos una receta antes de continuar',
      'step13c.error_stock':
        'Por falta de stock no podemos servir las recetas que habías elegido. Te llevamos al selector para que elijas otras.',
      'step13c.mobile_cta_email_required': 'Introduce tu email para continuar.',
      'step13c.mobile_cta_email_invalid': 'Revisa el formato del email para continuar.',

      // ── Gato (Próximamente) ────────────────────────────────────────────────
      'gato.title_v2': 'Próximamente para {nombre}',
      'gato.notify_button_v2': '¡Quiero que me aviséis cuando salga!',
      'gato.subscribed_v2': '¡Perfecto! Te avisaremos en cuanto esté listo.',
      'gato.back_to_pet': 'Volver a elegir mascota',
      'gato.email_placeholder': 'Tu email',
      'gato.logo_home_aria': 'Volver al inicio de la calculadora',

      // ── Images (URLs editables) ───────────────────────────────────────────
      'step1.image.dog': '',
      'step1.image.cat': '',
      'step1.image.logo': '',
    },
  },
  recipe_recommendation_rules: {
    enabled: true,
    max_preselected: 3,
    breed_size_ranges: {
      toy: { min_kg: 0, max_kg: 5 },
      pequeno: { min_kg: 5, max_kg: 10 },
      mediano: { min_kg: 10, max_kg: 25 },
      grande: { min_kg: 25, max_kg: 45 },
      gigante: { min_kg: 45, max_kg: 999 },
    },
    breed_size_overrides: {},
    scoring_weights: {
      pathology_match: 10,
      age_match: 4,
      body_condition_match: 4,
      activity_match: 3,
      breed_size_match: 3,
      base_priority: 1,
    },
    recipe_profiles: {},
  },
  supplement_rules: {
    enabled: true,
    max_recommended: 3,
    max_selectable: 4,
    strong_recommendation_threshold: 15,
    initial_visible_count: 4,
    scoring_weights: {
      pathology_match: 10,
      age_match: 4,
      body_condition_match: 4,
      activity_match: 3,
      breed_size_match: 3,
      base_priority: 1,
    },
    // El catálogo de suplementos viene del backend (/app-config, servido desde la
    // BD de productos + los channel bindings). El fallback offline NO lo duplica:
    // se eliminaron los suplementos hardcodeados (legacy, keyed por shopify_variant_id).
    supplements: [],
  },
  breed_database: {
    breeds: {},
  },
  products: [],
  checkout_available: true,
};
